/**
 * サンプルデータ
 * テストや開発時に使用するサンプルデータ
 * 原体験に基づく学生の価値観フローに沿った内容
 * アクティブノードと非アクティブノードの両方を含む
 */

import { TopicNode, Status } from '@/types/nodeTypes';
import { TopicEdge } from '@/types/edgeTypes';
import { Phase } from '@/types/phaseTypes';

/**
 * サンプルのフェーズ定義
 */
export const samplePhases: Phase[] = [
  {
    id: "fact_action",
    name: "事実・行動",
    description: "実際に行った行動や経験の事実",
    color: "#4287f5" // 青色
  },
  {
    id: "fact_detail",
    name: "事実・行動の具体",
    description: "行動や経験の詳細な内容",
    color: "#3db063" // 明度を落とした緑色（元は #42f584）
  },
  {
    id: "thought_feeling",
    name: "思考・感情",
    description: "経験から生じた考えや感情",
    color: "#f5a742" // オレンジ色
  },
  {
    id: "value",
    name: "価値観",
    description: "重視する価値や考え方",
    color: "#f54242" // 赤色
  },
  {
    id: "career_view",
    name: "キャリア観",
    description: "将来のキャリアに関する考え方",
    color: "#9c42f5" // 紫色
  },
  {
    id: "company",
    name: "紹介企業",
    description: "マッチする可能性のある企業",
    color: "#42c5f5" // 水色
  }
];

/**
 * サンプルのトランスクリプト
 */
export const sampleTranscript = `
インタビュアー: 今日はお時間をいただきありがとうございます。これまでのあなたの経験や、どのようなことに価値を感じているかについて伺っていきたいと思います。

学生: よろしくお願いします。

インタビュアー: 学生時代に特に力を入れて取り組んだことはありますか？

学生: 私は大学3年生のときにゼミのプロジェクトでチームリーダーを務めました。地域の高齢化問題に対する解決策を提案するプロジェクトで、メンバー8人を統括して約6ヶ月間活動しました。他のチームより2倍の時間をかけて地域の方々へのインタビューを実施し、延べ50人以上の高齢者の方から話を聞きました。

インタビュアー: 大変な取り組みだったと思いますが、具体的にどのようなことを行ったのですか？

学生: チームメンバーの意見がバラバラで進まない状況があったので、週に2回の定例ミーティングを設定し、毎回必ずアジェンダを事前に共有するようにしました。また、インタビューから得られた情報を整理するためのフレームワークを自分で考案して、データを構造化して分析しやすくしました。さらに、メンバーの得意分野を活かせるように役割分担を再設計して、全員が強みを発揮できる環境を整えました。

インタビュアー: そのような取り組みを通して、どのように感じましたか？

学生: 最初は意見の対立もあり苦労しましたが、徐々にチームの連携が良くなっていくのを実感できたときは本当に嬉しかったです。特に、自分が考えたフレームワークを使ってメンバー全員がスムーズに情報を共有できるようになったときは、自分の工夫が役立ったことに大きな達成感を感じました。また、最終発表会で教授から「非常に構造化された分析だ」と評価されたことは自信につながりました。

インタビュアー: 他のメンバーからの反応はいかがでしたか？

学生: プロジェクト終了後、何人かのメンバーから「あなたがいなかったらプロジェクトは成功しなかった」と言ってもらえて、本当に嬉しかったです。また、私が作ったフレームワークが他のゼミでも使われるようになったと後輩から聞いたときは、自分の取り組みが形として残ったことに大きな満足感を覚えました。

インタビュアー: そのような経験から、あなたが大切にしている価値観はありますか？

学生: 一番大切にしているのは「継続的な成長」です。このプロジェクトでも最初はうまくいきませんでしたが、試行錯誤しながら少しずつ改善していくプロセスが私には合っていると感じました。また、「人の役に立つこと」も重要だと思っています。自分のアイデアや行動が誰かの助けになるとき、大きなやりがいを感じます。さらに、「他者から認められること」や「新しいことへの挑戦」も私の価値観の一つです。未知の問題に取り組むときのわくわく感が好きですね。

インタビュアー: 将来のキャリアについてはどのように考えていますか？

学生: 専門性を高めながら社会課題の解決に関わりたいと考えています。特にデータ分析や問題解決の手法を磨きながら、社会や組織の課題解決に貢献できる仕事がしたいです。プロジェクトで感じたように、複雑な問題を構造化して解決する過程にやりがいを感じるので、コンサルティングの仕事に興味があります。また、創造性を活かして新しい価値を生み出せるような仕事にも関心があります。自分のアイデアでサービスや製品を改善することに喜びを感じるので、クリエイティブな側面も大切にしたいと思っています。

インタビュアー: 具体的に興味のある業界や企業はありますか？

学生: コンサルティングファームでは戦略系のコンサルティング会社に興味があります。特にマッキンゼーやベイン・アンド・カンパニーのような、社会的インパクトのあるプロジェクトも行っている企業に関心があります。テクノロジー企業では、GoogleやAmazonのようなデータ分析を強みとしている企業や、あるいはヘルスケア領域でデータを活用するヘルステック企業にも興味があります。自分の分析スキルや創造性を活かして社会に貢献できる場所で働きたいと思っています。

インタビュアー: あなたの強みは何だと思いますか？

学生: 私の強みは、複雑な問題を整理して構造化する力だと思います。先ほどのプロジェクトでも、多くの情報を整理するフレームワークを作ることで、チーム全体の理解を促進できました。また、異なる意見や背景を持つ人たちの間に立って調整する力も自分の強みだと感じています。さらに、諦めずに改善し続ける粘り強さも持っていると思います。失敗しても学びに変えて次に活かすことを心がけています。
`;

/**
 * サンプルノード
 * コンテンツの数を0, 1, 2, 3, 10, 15と多様化
 */
export const sampleNodes: TopicNode[] = [
  // コンテンツ0個（非アクティブ）
  {
    id: "high_effort",
    title: "高い水準で努力をした",
    phaseId: "fact_action",
    defaultImportance: 4,
    extractionPrompt: "会話から相手が高い水準で努力した経験や、通常以上の努力を続けた体験に関する言及を抽出してください。回数、期間、他者との比較など具体的な描写を含めてください。",
    contents: [],
    content: "",
    importance: 4,
    status: "partial" as Status,
    error: null,
    active: false,
    position: { x: 100, y: 100 },
    width: 250,
    height: 150
  },
  
  // コンテンツ1個
  {
    id: "problem_solving",
    title: "自ら問題解決に取り組んだ",
    phaseId: "fact_action",
    defaultImportance: 4,
    extractionPrompt: "会話から相手が自ら課題を見つけ、問題解決に取り組んだ経験に関する言及を抽出してください。問題の内容、解決プロセス、成果などを含めてください。",
    contents: [
      { 
        content: "地域の高齢化問題に対する解決策を提案するプロジェクトで、チームメンバーの意見がバラバラで進まない状況があったので、改善に取り組みました。", 
        timestamp: "00:04:30" 
      }
    ],
    content: "地域の高齢化問題に対する解決策を提案するプロジェクトで、チームメンバーの意見がバラバラで進まない状況があったので、改善に取り組みました。",
    importance: 4,
    status: "success" as Status,
    error: null,
    active: true,
    position: { x: 400, y: 100 },
    width: 250,
    height: 150
  },
  
  // コンテンツ2個
  {
    id: "team_contribution",
    title: "チームに貢献した",
    phaseId: "fact_action",
    defaultImportance: 3,
    extractionPrompt: "会話から相手がチームやグループに貢献した経験に関する言及を抽出してください。具体的な役割、行動、チームへの影響などを含めてください。",
    contents: [
      { 
        content: "私は大学3年生のときにゼミのプロジェクトでチームリーダーを務めました。地域の高齢化問題に対する解決策を提案するプロジェクトで、メンバー8人を統括して約6ヶ月間活動しました。", 
        timestamp: "00:02:45" 
      },
      {
        content: "メンバーの得意分野を活かせるように役割分担を再設計して、全員が強みを発揮できる環境を整えました。",
        timestamp: "00:05:45"
      }
    ],
    content: "私は大学3年生のときにゼミのプロジェクトでチームリーダーを務めました。地域の高齢化問題に対する解決策を提案するプロジェクトで、メンバー8人を統括して約6ヶ月間活動しました。",
    importance: 3,
    status: "success" as Status,
    error: null,
    active: true,
    position: { x: 700, y: 100 },
    width: 250,
    height: 150
  },
  
  // コンテンツ3個
  {
    id: "continuous_effort",
    title: "継続的な努力",
    phaseId: "fact_detail",
    defaultImportance: 3,
    extractionPrompt: "会話から相手が長期間にわたって継続的に努力した詳細について言及を抽出してください。期間、頻度、困難を乗り越えた方法などを含めてください。",
    contents: [
      { 
        content: "約6ヶ月間活動しました。他のチームより2倍の時間をかけて地域の方々へのインタビューを実施しました。", 
        timestamp: "00:03:20" 
      },
      {
        content: "延べ50人以上の高齢者の方から話を聞きました。",
        timestamp: "00:03:30"
      },
      {
        content: "週に2回の定例ミーティングを設定し、毎回必ずアジェンダを事前に共有するようにしました。",
        timestamp: "00:05:10"
      }
    ],
    content: "約6ヶ月間活動しました。他のチームより2倍の時間をかけて地域の方々へのインタビューを実施し、延べ50人以上の高齢者の方から話を聞きました。",
    importance: 3,
    status: "success" as Status,
    error: null,
    active: true,
    position: { x: 100, y: 300 },
    width: 250,
    height: 150
  },
  
  // コンテンツ10個
  {
    id: "initiative_action",
    title: "自ら率先した行動",
    phaseId: "fact_detail",
    defaultImportance: 3,
    extractionPrompt: "会話から相手が自ら率先して行動を起こした具体的な内容に関する言及を抽出してください。きっかけ、判断理由、具体的な行動などを含めてください。",
    contents: [
      {
        content: "週に2回の定例ミーティングを設定しました。",
        timestamp: "00:05:05"
      },
      {
        content: "毎回必ずアジェンダを事前に共有するようにしました。",
        timestamp: "00:05:10"
      },
      {
        content: "インタビューから得られた情報を整理するためのフレームワークを自分で考案しました。",
        timestamp: "00:05:25"
      },
      {
        content: "データを構造化して分析しやすくしました。",
        timestamp: "00:05:30"
      },
      {
        content: "メンバーの得意分野を活かせるように役割分担を再設計しました。",
        timestamp: "00:05:40"
      },
      {
        content: "全員が強みを発揮できる環境を整えました。",
        timestamp: "00:05:45"
      },
      {
        content: "チームの進捗状況を可視化するためのダッシュボードを作成しました。",
        timestamp: "00:06:00"
      },
      {
        content: "アイデアを出し合うためのブレインストーミングセッションを企画しました。",
        timestamp: "00:06:15"
      },
      {
        content: "他のチームとの情報共有会を開催しました。",
        timestamp: "00:06:30"
      },
      {
        content: "最終発表に向けてリハーサルを何度も実施しました。",
        timestamp: "00:06:45"
      }
    ],
    content: "週に2回の定例ミーティングを設定し、毎回必ずアジェンダを事前に共有するようにしました。",
    importance: 3,
    status: "success" as Status,
    error: null,
    active: true,
    position: { x: 400, y: 300 },
    width: 250,
    height: 150
  },
  
  // コンテンツ15個
  {
    id: "improvement_action",
    title: "改善のための行動",
    phaseId: "fact_detail",
    defaultImportance: 3,
    extractionPrompt: "会話から相手が何かを改善するために具体的に行った行動に関する言及を抽出してください。改善対象、行動内容、工夫した点などを含めてください。",
    contents: [
      { 
        content: "インタビューから得られた情報を整理するためのフレームワークを自分で考案しました。", 
        timestamp: "00:05:25" 
      },
      {
        content: "データを構造化して分析しやすくしました。",
        timestamp: "00:05:30"
      },
      {
        content: "メンバーの得意分野を活かせるように役割分担を再設計しました。",
        timestamp: "00:05:40"
      },
      {
        content: "全員が強みを発揮できる環境を整えました。",
        timestamp: "00:05:45"
      },
      {
        content: "ミーティングの効率化のためにタイムキーパーを導入しました。",
        timestamp: "00:06:00"
      },
      {
        content: "フィードバックを収集するための匿名アンケートを実施しました。",
        timestamp: "00:06:10"
      },
      {
        content: "インタビュー質問項目を改良してより具体的な回答を引き出せるようにしました。",
        timestamp: "00:06:20"
      },
      {
        content: "議事録のテンプレートを作成して情報の漏れを防ぎました。",
        timestamp: "00:06:30"
      },
      {
        content: "チーム内のコミュニケーションツールを導入しました。",
        timestamp: "00:06:40"
      },
      {
        content: "定期的に1on1ミーティングを行ってメンバーの課題を早期に発見するようにしました。",
        timestamp: "00:06:50"
      },
      {
        content: "データの可視化方法を改善して直感的に理解できるようにしました。",
        timestamp: "00:07:00"
      },
      {
        content: "中間報告会を開いて外部からのフィードバックを取り入れました。",
        timestamp: "00:07:10"
      },
      {
        content: "過去のプロジェクト事例を研究して成功パターンを分析しました。",
        timestamp: "00:07:20"
      },
      {
        content: "チームメンバーのモチベーション維持のための小さな成功体験を設計しました。",
        timestamp: "00:07:30"
      },
      {
        content: "プロジェクト完了後に振り返りを行って改善点を文書化しました。",
        timestamp: "00:07:40"
      }
    ],
    content: "インタビューから得られた情報を整理するためのフレームワークを自分で考案して、データを構造化して分析しやすくしました。さらに、メンバーの得意分野を活かせるように役割分担を再設計して、全員が強みを発揮できる環境を整えました。",
    importance: 3,
    status: "success" as Status,
    error: null,
    active: true,
    position: { x: 700, y: 300 },
    width: 250,
    height: 150
  },
  
  // 思考・感情ノード（すべてアクティブ）
  {
    id: "fulfillment",
    title: "達成感・充実感",
    phaseId: "thought_feeling",
    defaultImportance: 4,
    extractionPrompt: "会話から相手が経験した達成感や充実感に関する言及を抽出してください。具体的な感情の描写、その感情が生じた状況などを含めてください。",
    contents: [
      { 
        content: "自分が考えたフレームワークを使ってメンバー全員がスムーズに情報を共有できるようになったときは、自分の工夫が役立ったことに大きな達成感を感じました。", 
        timestamp: "00:06:45" 
      }
    ],
    content: "自分が考えたフレームワークを使ってメンバー全員がスムーズに情報を共有できるようになったときは、自分の工夫が役立ったことに大きな達成感を感じました。",
    importance: 4,
    status: "success" as Status,
    error: null,
    active: true,
    position: { x: 100, y: 500 },
    width: 250,
    height: 150
  },
  {
    id: "pride",
    title: "誇り・自信",
    phaseId: "thought_feeling",
    defaultImportance: 3,
    extractionPrompt: "会話から相手が感じた誇りや自信に関する言及を抽出してください。何に対して誇りや自信を持ったのか、その感情の強さなどを含めてください。",
    contents: [
      { 
        content: "最終発表会で教授から「非常に構造化された分析だ」と評価されたことは自信につながりました。", 
        timestamp: "00:07:20" 
      }
    ],
    content: "最終発表会で教授から「非常に構造化された分析だ」と評価されたことは自信につながりました。",
    importance: 3,
    status: "success" as Status,
    error: null,
    active: true,
    position: { x: 400, y: 500 },
    width: 250,
    height: 150
  },
  {
    id: "satisfaction_from_others",
    title: "他者からの反応による満足感",
    phaseId: "thought_feeling",
    defaultImportance: 3,
    extractionPrompt: "会話から相手が他者からの反応や評価によって感じた満足感に関する言及を抽出してください。他者の反応、それに対する感情などを含めてください。",
    contents: [
      { 
        content: "プロジェクト終了後、何人かのメンバーから「あなたがいなかったらプロジェクトは成功しなかった」と言ってもらえて、本当に嬉しかったです。", 
        timestamp: "00:08:05" 
      },
      {
        content: "私が作ったフレームワークが他のゼミでも使われるようになったと後輩から聞いたときは、自分の取り組みが形として残ったことに大きな満足感を覚えました。",
        timestamp: "00:08:15"
      }
    ],
    content: "プロジェクト終了後、何人かのメンバーから「あなたがいなかったらプロジェクトは成功しなかった」と言ってもらえて、本当に嬉しかったです。また、私が作ったフレームワークが他のゼミでも使われるようになったと後輩から聞いたときは、自分の取り組みが形として残ったことに大きな満足感を覚えました。",
    importance: 3,
    status: "success" as Status,
    error: null,
    active: true,
    position: { x: 700, y: 500 },
    width: 250,
    height: 150
  },
  
  // 価値観ノード（一部非アクティブ）
  {
    id: "self_growth",
    title: "自己成長を重視する",
    phaseId: "value",
    defaultImportance: 4,
    extractionPrompt: "会話から相手が自己成長を重視していることを示す言及を抽出してください。成長に対する考え方、目標、成長のために行っていることなどを含めてください。",
    contents: [
      { 
        content: "一番大切にしているのは「継続的な成長」です。このプロジェクトでも最初はうまくいきませんでしたが、試行錯誤しながら少しずつ改善していくプロセスが私には合っていると感じました。", 
        timestamp: "00:10:20" 
      }
    ],
    content: "一番大切にしているのは「継続的な成長」です。このプロジェクトでも最初はうまくいきませんでしたが、試行錯誤しながら少しずつ改善していくプロセスが私には合っていると感じました。",
    importance: 4,
    status: "success" as Status,
    error: null,
    active: true,
    position: { x: 100, y: 700 },
    width: 250,
    height: 150
  },
  {
    id: "contribution_value",
    title: "貢献することに価値を見出す",
    phaseId: "value",
    defaultImportance: 4,
    extractionPrompt: "会話から相手が他者や組織に貢献することに価値を見出していることを示す言及を抽出してください。貢献の意義、貢献したいと思う対象などを含めてください。",
    contents: [
      { 
        content: "「人の役に立つこと」も重要だと思っています。自分のアイデアや行動が誰かの助けになるとき、大きなやりがいを感じます。", 
        timestamp: "00:10:45" 
      }
    ],
    content: "「人の役に立つこと」も重要だと思っています。自分のアイデアや行動が誰かの助けになるとき、大きなやりがいを感じます。",
    importance: 4,
    status: "success" as Status,
    error: null,
    active: true,
    position: { x: 400, y: 700 },
    width: 250,
    height: 150
  },
  {
    id: "recognition_value",
    title: "認められることに価値を見出す",
    phaseId: "value",
    defaultImportance: 3,
    extractionPrompt: "会話から相手が他者から認められることに価値を見出していることを示す言及を抽出してください。認められたい内容、理由などを含めてください。",
    contents: [
      { 
        content: "「他者から認められること」も私の価値観の一つです。", 
        timestamp: "00:11:00" 
      }
    ],
    content: "「他者から認められること」も私の価値観の一つです。",
    importance: 3,
    status: "success" as Status,
    error: null,
    active: true,
    position: { x: 700, y: 700 },
    width: 250,
    height: 150
  },
  {
    id: "challenge_value",
    title: "挑戦することに価値を見出す",
    phaseId: "value",
    defaultImportance: 3,
    extractionPrompt: "会話から相手が挑戦することに価値を見出していることを示す言及を抽出してください。挑戦に対する姿勢、どのような挑戦をしたいかなどを含めてください。",
    contents: [
      { 
        content: "「新しいことへの挑戦」も私の価値観の一つです。未知の問題に取り組むときのわくわく感が好きですね。", 
        timestamp: "00:11:10" 
      }
    ],
    content: "「新しいことへの挑戦」も私の価値観の一つです。未知の問題に取り組むときのわくわく感が好きですね。",
    importance: 3,
    status: "success" as Status,
    error: null,
    active: true,
    position: { x: 100, y: 900 },
    width: 250,
    height: 150
  },
  
  // キャリア観ノード（一部非アクティブ）
  {
    id: "professional_growth",
    title: "専門性を高めるキャリア",
    phaseId: "career_view",
    defaultImportance: 4,
    extractionPrompt: "会話から相手が専門性を高めることを重視するキャリア志向に関する言及を抽出してください。目指す専門分野、理由などを含めてください。",
    contents: [
      { 
        content: "専門性を高めながら社会課題の解決に関わりたいと考えています。特にデータ分析や問題解決の手法を磨きながら、社会や組織の課題解決に貢献できる仕事がしたいです。", 
        timestamp: "00:12:30" 
      }
    ],
    content: "専門性を高めながら社会課題の解決に関わりたいと考えています。特にデータ分析や問題解決の手法を磨きながら、社会や組織の課題解決に貢献できる仕事がしたいです。",
    importance: 4,
    status: "success" as Status,
    error: null,
    active: true,
    position: { x: 400, y: 900 },
    width: 250,
    height: 150
  },
  {
    id: "social_impact_career",
    title: "社会的影響力のあるキャリア",
    phaseId: "career_view",
    defaultImportance: 3,
    extractionPrompt: "会話から相手が社会的影響力のあるキャリアを望んでいることを示す言及を抽出してください。どのような影響を与えたいか、理由などを含めてください。",
    contents: [],
    content: "",
    importance: 3,
    status: "partial" as Status,
    error: null,
    active: false,
    position: { x: 700, y: 900 },
    width: 250,
    height: 150
  },
  {
    id: "creative_career",
    title: "創造性を活かすキャリア",
    phaseId: "career_view",
    defaultImportance: 3,
    extractionPrompt: "会話から相手が創造性を活かしたキャリアを望んでいることを示す言及を抽出してください。どのように創造性を発揮したいか、理由などを含めてください。",
    contents: [
      { 
        content: "また、創造性を活かして新しい価値を生み出せるような仕事にも関心があります。自分のアイデアでサービスや製品を改善することに喜びを感じるので、クリエイティブな側面も大切にしたいと思っています。", 
        timestamp: "00:13:00" 
      }
    ],
    content: "また、創造性を活かして新しい価値を生み出せるような仕事にも関心があります。自分のアイデアでサービスや製品を改善することに喜びを感じるので、クリエイティブな側面も大切にしたいと思っています。",
    importance: 3,
    status: "success" as Status,
    error: null,
    active: true,
    position: { x: 100, y: 1100 },
    width: 250,
    height: 150
  },
  
  // 紹介企業ノード（一部非アクティブ、エラー状態も含む）
  {
    id: "tech_company",
    title: "テクノロジー企業",
    phaseId: "company",
    defaultImportance: 3,
    extractionPrompt: "会話から相手がテクノロジー企業に関心を持っていることを示す言及を抽出してください。具体的な企業名、興味のある技術分野などを含めてください。",
    contents: [
      { 
        content: "テクノロジー企業では、GoogleやAmazonのようなデータ分析を強みとしている企業に興味があります。", 
        timestamp: "00:14:35" 
      }
    ],
    content: "テクノロジー企業では、GoogleやAmazonのようなデータ分析を強みとしている企業に興味があります。",
    importance: 3,
    status: "success" as Status,
    error: null,
    active: true,
    position: { x: 400, y: 1100 },
    width: 250,
    height: 150
  },
  {
    id: "consulting_company",
    title: "コンサルティング企業",
    phaseId: "company",
    defaultImportance: 3,
    extractionPrompt: "会話から相手がコンサルティング企業に関心を持っていることを示す言及を抽出してください。理由、興味のある分野などを含めてください。",
    contents: [],
    content: "",
    importance: 3,
    status: "partial" as Status,
    error: null,
    active: false,
    position: { x: 700, y: 1100 },
    width: 250,
    height: 150
  },
  {
    id: "health_tech_company",
    title: "ヘルステック企業",
    phaseId: "company",
    defaultImportance: 3,
    extractionPrompt: "会話から相手がヘルスケア領域のテクノロジー企業に関心を持っていることを示す言及を抽出してください。",
    contents: [],
    content: "",
    importance: 3,
    status: "error" as Status,
    error: {
      message: "抽出処理中にエラーが発生しました",
      code: "EXTRACTION_ERROR"
    },
    active: false,
    position: { x: 100, y: 1300 },
    width: 250,
    height: 150
  }
];

/**
 * サンプルエッジ
 * 一部のエッジは非アクティブに設定
 */
export const sampleEdges: TopicEdge[] = [
  // 事実・行動から事実・行動の具体へ（すべてアクティブ）
  {
    id: "high_effort_to_continuous_effort",
    source: "high_effort",
    target: "continuous_effort",
    question: "どのような継続的な努力をしましたか？",
    strength: "strong",
    extractionPrompt: "会話から、高い水準の努力の具体的な内容に関する言及を抽出してください。努力の方法、期間、頻度などの詳細を探してください。",
    contents: [],
    content: "",
    status: "partial" as Status,
    error: null,
    active: false
  },
  {
    id: "problem_solving_to_initiative_action",
    source: "problem_solving",
    target: "initiative_action",
    question: "問題解決のために具体的にどのような率先行動をとりましたか？",
    strength: "strong",
    extractionPrompt: "会話から、問題解決のために取った具体的な率先行動に関する言及を抽出してください。行動の内容、きっかけ、プロセスなどを探してください。",
    contents: [
      {
        content: "チームメンバーの意見がバラバラで進まない状況があったので、週に2回の定例ミーティングを設定し、毎回必ずアジェンダを事前に共有するようにしました。",
        timestamp: "00:05:10"
      }
    ],
    content: "チームメンバーの意見がバラバラで進まない状況があったので、週に2回の定例ミーティングを設定し、毎回必ずアジェンダを事前に共有するようにしました。",
    status: "success" as Status,
    error: null,
    active: true
  },
  {
    id: "team_contribution_to_improvement_action",
    source: "team_contribution",
    target: "improvement_action",
    question: "チームに貢献するために具体的にどのような改善行動をとりましたか？",
    strength: "medium",
    extractionPrompt: "会話から、チームへの貢献のために行った具体的な改善行動に関する言及を抽出してください。改善内容、プロセス、成果などを探してください。",
    contents: [
      {
        content: "メンバーの得意分野を活かせるように役割分担を再設計して、全員が強みを発揮できる環境を整えました。",
        timestamp: "00:05:50"
      }
    ],
    content: "メンバーの得意分野を活かせるように役割分担を再設計して、全員が強みを発揮できる環境を整えました。",
    status: "success" as Status,
    error: null,
    active: true
  },
  
  // 事実・行動の具体から思考・感情へ（すべてアクティブ）
  {
    id: "continuous_effort_to_fulfillment",
    source: "continuous_effort",
    target: "fulfillment",
    question: "継続的な努力からどのような達成感を得ましたか？",
    strength: "strong",
    extractionPrompt: "会話から、継続的な努力によって得られた達成感や充実感に関する言及を抽出してください。具体的な感情の描写、感情が生じた瞬間などを探してください。",
    contents: [
      {
        content: "徐々にチームの連携が良くなっていくのを実感できたときは本当に嬉しかったです。",
        timestamp: "00:06:30"
      }
    ],
    content: "徐々にチームの連携が良くなっていくのを実感できたときは本当に嬉しかったです。",
    status: "success" as Status,
    error: null,
    active: true
  },
  {
    id: "initiative_action_to_pride",
    source: "initiative_action",
    target: "pride",
    question: "率先した行動からどのような誇りや自信を得ましたか？",
    strength: "medium",
    extractionPrompt: "会話から、率先した行動によって得られた誇りや自信に関する言及を抽出してください。何に対して誇りを感じたか、自信につながった理由などを探してください。",
    contents: [
      {
        content: "最終発表会で教授から「非常に構造化された分析だ」と評価されたことは自信につながりました。",
        timestamp: "00:07:20"
      }
    ],
    content: "最終発表会で教授から「非常に構造化された分析だ」と評価されたことは自信につながりました。",
    status: "success" as Status,
    error: null,
    active: true
  },
  {
    id: "improvement_action_to_satisfaction_from_others",
    source: "improvement_action",
    target: "satisfaction_from_others",
    question: "改善行動によって得られた他者からの反応はどうでしたか？",
    strength: "medium",
    extractionPrompt: "会話から、改善行動に対する他者からの反応とそれによる満足感に関する言及を抽出してください。他者の具体的な反応、それに対する感情などを探してください。",
    contents: [
      {
        content: "プロジェクト終了後、何人かのメンバーから「あなたがいなかったらプロジェクトは成功しなかった」と言ってもらえて、本当に嬉しかったです。また、私が作ったフレームワークが他のゼミでも使われるようになったと後輩から聞いたときは、自分の取り組みが形として残ったことに大きな満足感を覚えました。",
        timestamp: "00:08:10"
      }
    ],
    content: "プロジェクト終了後、何人かのメンバーから「あなたがいなかったらプロジェクトは成功しなかった」と言ってもらえて、本当に嬉しかったです。また、私が作ったフレームワークが他のゼミでも使われるようになったと後輩から聞いたときは、自分の取り組みが形として残ったことに大きな満足感を覚えました。",
    status: "success" as Status,
    error: null,
    active: true
  },
  
  // 思考・感情から価値観へ（一部非アクティブ）
  {
    id: "fulfillment_to_self_growth",
    source: "fulfillment",
    target: "self_growth",
    question: "達成感・充実感は自己成長への価値観にどうつながりましたか？",
    strength: "strong",
    extractionPrompt: "会話から、達成感や充実感が自己成長を重視する価値観につながったことに関する言及を抽出してください。価値観の形成過程、価値観の具体的な内容などを探してください。",
    contents: [
      {
        content: "一番大切にしているのは「継続的な成長」です。このプロジェクトでも最初はうまくいきませんでしたが、試行錯誤しながら少しずつ改善していくプロセスが私には合っていると感じました。",
        timestamp: "00:10:20"
      }
    ],
    content: "一番大切にしているのは「継続的な成長」です。このプロジェクトでも最初はうまくいきませんでしたが、試行錯誤しながら少しずつ改善していくプロセスが私には合っていると感じました。",
    status: "success" as Status,
    error: null,
    active: true
  },
  {
    id: "pride_to_challenge_value",
    source: "pride",
    target: "challenge_value",
    question: "誇り・自信は挑戦することへの価値観にどうつながりましたか？",
    strength: "medium",
    extractionPrompt: "会話から、誇りや自信が挑戦することを重視する価値観につながったことに関する言及を抽出してください。価値観の形成過程、挑戦に対する姿勢などを探してください。",
    contents: [
      {
        content: "「新しいことへの挑戦」も私の価値観の一つです。未知の問題に取り組むときのわくわく感が好きですね。",
        timestamp: "00:11:10"
      }
    ],
    content: "「新しいことへの挑戦」も私の価値観の一つです。未知の問題に取り組むときのわくわく感が好きですね。",
    status: "success" as Status,
    error: null,
    active: true
  },
  {
    id: "satisfaction_from_others_to_contribution_value",
    source: "satisfaction_from_others",
    target: "contribution_value",
    question: "他者からの反応による満足感は貢献することへの価値観にどうつながりましたか？",
    strength: "strong",
    extractionPrompt: "会話から、他者からの反応による満足感が貢献することを重視する価値観につながったことに関する言及を抽出してください。価値観の形成過程、貢献したい理由などを探してください。",
    contents: [
      {
        content: "「人の役に立つこと」も重要だと思っています。自分のアイデアや行動が誰かの助けになるとき、大きなやりがいを感じます。",
        timestamp: "00:10:45"
      }
    ],
    content: "「人の役に立つこと」も重要だと思っています。自分のアイデアや行動が誰かの助けになるとき、大きなやりがいを感じます。",
    status: "success" as Status,
    error: null,
    active: true
  },
  {
    id: "satisfaction_from_others_to_recognition_value",
    source: "satisfaction_from_others",
    target: "recognition_value",
    question: "他者からの反応による満足感は認められることへの価値観にどうつながりましたか？",
    strength: "medium",
    extractionPrompt: "会話から、他者からの反応による満足感が認められることを重視する価値観につながったことに関する言及を抽出してください。価値観の形成過程、認められたい理由などを探してください。",
    contents: [
      {
        content: "「他者から認められること」も私の価値観の一つです。",
        timestamp: "00:11:00"
      }
    ],
    content: "「他者から認められること」も私の価値観の一つです。",
    status: "success" as Status,
    error: null,
    active: true
  },
  
  // 価値観からキャリア観へ（一部非アクティブ）
  {
    id: "self_growth_to_professional_growth",
    source: "self_growth",
    target: "professional_growth",
    question: "自己成長の価値観は専門性を高めるキャリア志向にどうつながりますか？",
    strength: "strong",
    extractionPrompt: "会話から、自己成長を重視する価値観が専門性を高めるキャリア志向につながることに関する言及を抽出してください。キャリア選択の理由、専門分野に対する考えなどを探してください。",
    contents: [
      {
        content: "専門性を高めながら社会課題の解決に関わりたいと考えています。特にデータ分析や問題解決の手法を磨きながら、社会や組織の課題解決に貢献できる仕事がしたいです。",
        timestamp: "00:12:30"
      }
    ],
    content: "専門性を高めながら社会課題の解決に関わりたいと考えています。特にデータ分析や問題解決の手法を磨きながら、社会や組織の課題解決に貢献できる仕事がしたいです。",
    status: "success" as Status,
    error: null,
    active: true
  },
  {
    id: "contribution_value_to_social_impact_career",
    source: "contribution_value",
    target: "social_impact_career",
    question: "貢献の価値観は社会的影響力のあるキャリア志向にどうつながりますか？",
    strength: "strong",
    extractionPrompt: "会話から、貢献することを重視する価値観が社会的影響力のあるキャリア志向につながることに関する言及を抽出してください。社会への貢献方法、影響を与えたい分野などを探してください。",
    contents: [],
    content: "",
    status: "partial" as Status,
    error: null,
    active: false
  },
  {
    id: "challenge_value_to_creative_career",
    source: "challenge_value",
    target: "creative_career",
    question: "挑戦の価値観は創造性を活かすキャリア志向にどうつながりますか？",
    strength: "medium",
    extractionPrompt: "会話から、挑戦することを重視する価値観が創造性を活かすキャリア志向につながることに関する言及を抽出してください。創造的なキャリアへの興味、理由などを探してください。",
    contents: [
      {
        content: "また、創造性を活かして新しい価値を生み出せるような仕事にも関心があります。自分のアイデアでサービスや製品を改善することに喜びを感じるので、クリエイティブな側面も大切にしたいと思っています。",
        timestamp: "00:13:00"
      }
    ],
    content: "また、創造性を活かして新しい価値を生み出せるような仕事にも関心があります。自分のアイデアでサービスや製品を改善することに喜びを感じるので、クリエイティブな側面も大切にしたいと思っています。",
    status: "success" as Status,
    error: null,
    active: true
  },
  
  // キャリア観から紹介企業へ（一部非アクティブ）
  {
    id: "professional_growth_to_tech_company",
    source: "professional_growth",
    target: "tech_company",
    question: "専門性を高めるキャリア志向はテクノロジー企業への関心にどうつながりますか？",
    strength: "medium",
    extractionPrompt: "会話から、専門性を高めるキャリア志向がテクノロジー企業への関心につながることに関する言及を抽出してください。関心のある企業タイプ、理由などを探してください。",
    contents: [
      {
        content: "テクノロジー企業では、GoogleやAmazonのようなデータ分析を強みとしている企業に興味があります。",
        timestamp: "00:14:35"
      }
    ],
    content: "テクノロジー企業では、GoogleやAmazonのようなデータ分析を強みとしている企業に興味があります。",
    status: "success" as Status,
    error: null,
    active: true
  },
  {
    id: "social_impact_career_to_consulting_company",
    source: "social_impact_career",
    target: "consulting_company",
    question: "社会的影響力のあるキャリア志向はコンサルティング企業への関心にどうつながりますか？",
    strength: "medium",
    extractionPrompt: "会話から、社会的影響力のあるキャリア志向がコンサルティング企業への関心につながることに関する言及を抽出してください。関心のある企業タイプ、理由などを探してください。",
    contents: [],
    content: "",
    status: "partial" as Status,
    error: null,
    active: false
  },
  {
    id: "creative_career_to_tech_company",
    source: "creative_career",
    target: "tech_company",
    question: "創造性を活かすキャリア志向はテクノロジー企業への関心にどうつながりますか？",
    strength: "medium",
    extractionPrompt: "会話から、創造性を活かすキャリア志向がテクノロジー企業への関心につながることに関する言及を抽出してください。関心のある企業タイプ、理由などを探してください。",
    contents: [
      {
        content: "自分のアイデアでサービスや製品を改善することに喜びを感じるので、GoogleやAmazonのようなテクノロジー企業に興味があります。",
        timestamp: "00:14:40"
      }
    ],
    content: "自分のアイデアでサービスや製品を改善することに喜びを感じるので、GoogleやAmazonのようなテクノロジー企業に興味があります。",
    status: "success" as Status,
    error: null,
    active: true
  },
  // エラー状態のエッジ
  {
    id: "professional_growth_to_health_tech_company",
    source: "professional_growth",
    target: "health_tech_company",
    question: "専門性を高めるキャリア志向はヘルステック企業への関心にどうつながりますか？",
    strength: "weak",
    extractionPrompt: "会話から、専門性を高めるキャリア志向がヘルステック企業への関心につながることに関する言及を抽出してください。",
    contents: [],
    content: "",
    status: "error" as Status,
    error: {
      message: "抽出処理中にエラーが発生しました",
      code: "EXTRACTION_ERROR"
    },
    active: false
  }
];

/**
 * サンプルマップデータ
 */
export const sampleMapData = {
  id: "sample_map_001",
  name: "サンプルマップ：原体験に基づく学生の価値観",
  nodes: sampleNodes,
  edges: sampleEdges,
  transcript: sampleTranscript,
  phases: samplePhases  // フェーズ情報を追加
};